import { useCallback, useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { Alert, Button, Stack, Typography } from '@mui/material';
import type { WorkflowRequest, WorkflowTask } from '../model';
import type { WorkflowPort } from '../port';
import type { WorkflowApprovalSettingsPort } from '../approval-settings/public';
import type { EmployeeDirectoryPort } from '../../people/public';
import { ConfirmDialog, DefinitionList, DetailDrawer, DetailDrawerBody, FormTextField, SectionCard, type WorkspaceSurfacePresentation } from '../../../design-system';
import { registerNavigationGuard } from '../../../shared/navigationGuard';
import { fixtureRequests } from '../fixtures/workflowPageFixtures';
import { isAmbiguousApiMutationFailure, workflowErrorCode, workflowErrorStatus } from '../workflowPageSupport';
import { formatWorkflowDate, workflowTaskTitle } from '../models/workflowRequestPageModel';
import { workflowRequesterLabel, workflowTaskDetailError, workflowTaskDocumentId, workflowTaskWaitingLabel } from '../models/workflowRequestListModel';
import { WorkflowRequestDocumentWorkspace, workflowRequestSummaryFromInboxItem } from './WorkflowRequestDocumentWorkspace';
import type { WorkflowCommentAttempt } from '../WorkflowActivityTimeline';
import { TimeOffCancellationSummary } from './TimeOffCancellationSummary';

export type WorkflowTaskDecision = 'approve' | 'reject' | 'requestChanges';
type Attempt = { action: WorkflowTaskDecision; comment: string; key: string };
export type WorkflowTaskReviewProps = {
  task?: WorkflowTask;
  navigationRef?: RefObject<((navigate: () => void) => void) | null>;
  port: WorkflowPort;
  approvalSettingsPort: WorkflowApprovalSettingsPort;
  directoryPort: EmployeeDirectoryPort;
  fixtureMode: boolean;
  canAct: boolean;
  canComment: boolean;
  presentation: WorkspaceSurfacePresentation;
  onPresentationChange: (value: WorkspaceSurfacePresentation) => void;
  onClose: () => void;
  onComplete: (task: WorkflowTask, action: WorkflowTaskDecision, message: string) => void;
  onRefresh: () => void;
  onBusyChange?: (busy: boolean) => void;
  onOpenReference?: (id: string) => void;
  legacyDetail?: ReactNode;
  legacyLoading?: boolean;
  legacyError?: string;
  onRetryLegacy?: () => void;
  actions?: {
    approve: (stepId: string, comment: string, key: string) => Promise<void>;
    reject: (stepId: string, comment: string, key: string) => Promise<void>;
  };
};

/** 직원 할 일과 관리자 결재함이 공유하는 단건 검토·결정 수명입니다. */
export function WorkflowTaskReview({ task, navigationRef, port, approvalSettingsPort, directoryPort, fixtureMode, canAct: allowed,
  canComment, presentation, onPresentationChange, onClose, onComplete, onRefresh, onBusyChange, onOpenReference,
  legacyDetail, legacyLoading = false, legacyError, onRetryLegacy, actions }: WorkflowTaskReviewProps) {
  const [request, setRequest] = useState<WorkflowRequest>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [verifiedAssignment, setVerifiedAssignment] = useState<WorkflowTask>();
  const [assignmentLoading, setAssignmentLoading] = useState(false);
  const [assignmentError, setAssignmentError] = useState('');
  const [retry, setRetry] = useState(0);
  const [activityRefreshKey, setActivityRefreshKey] = useState(0);
  const [comment, setComment] = useState('');
  const [actionError, setActionError] = useState('');
  const [commentError, setCommentError] = useState('');
  const [confirmation, setConfirmation] = useState<Exclude<WorkflowTaskDecision, 'approve'>>();
  const opinionInput = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const [conflict, setConflict] = useState(false);
  const [pending, setPending] = useState(false);
  const [uncertain, setUncertain] = useState<Attempt>();
  const [commentAttempt, setCommentAttempt] = useState<WorkflowCommentAttempt>();
  const [commentBusy, setCommentBusy] = useState(false);
  const drafts = useRef(new Map<string, string>());
  const attempts = useRef(new Map<string, Attempt>());
  const commentAttempts = useRef(new Map<string, WorkflowCommentAttempt>());
  const pendingRef = useRef(false);
  const pendingMutation = useRef('');
  const skipGuard = useRef(false);
  const sequence = useRef(0);
  const taskRef = useRef(task);
  taskRef.current = task;
  const documentId = task && workflowTaskDocumentId(task);
  const busy = pending || commentBusy;
  const retainAndNavigate = useCallback((callback: () => void) => {
    skipGuard.current = true;
    try { callback(); } finally { skipGuard.current = false; }
  }, []);

  useEffect(() => { onBusyChange?.(busy); }, [busy, onBusyChange]);
  useEffect(() => {
    const guard = () => skipGuard.current || (!busy && (!([...drafts.current.values()].some(value => value.trim()))
      || window.confirm('전송하지 않은 의견이 있습니다. 이 화면을 나갈까요?')));
    const unregister = registerNavigationGuard(guard);
    const unload = (event: BeforeUnloadEvent) => {
      if (busy || [...drafts.current.values()].some(value => value.trim())) { event.preventDefault(); event.returnValue = ''; }
    };
    window.addEventListener('beforeunload', unload);
    return () => { unregister(); window.removeEventListener('beforeunload', unload); };
  }, [busy]);
  useEffect(() => {
    const current = ++sequence.current;
    setRequest(undefined); setError(''); setActionError(''); setCommentError(''); setConfirmation(undefined); setConflict(false); setCommentBusy(false);
    setVerifiedAssignment(undefined); setAssignmentError(''); setAssignmentLoading(false);
    const attempt = task && attempts.current.get(task.stepId);
    const savedCommentAttempt = task && commentAttempts.current.get(task.stepId);
    setCommentAttempt(savedCommentAttempt);
    setUncertain(attempt); setComment(attempt?.comment ?? savedCommentAttempt?.body ?? (task && drafts.current.get(task.stepId)) ?? '');
    if (!task || !documentId) { setLoading(false); return; }
    setLoading(true);
    const response = fixtureMode ? Promise.resolve(fixtureRequests.find(value => value.id === documentId)) : port.getWorkflowRequest(documentId);
    void response.then(value => {
      if (current !== sequence.current) return;
      if (!value || value.id !== documentId) throw new Error('선택한 요청 상세를 찾을 수 없습니다.');
      setRequest(value);
    }).catch((reason: unknown) => {
      if (current === sequence.current) setError(reason instanceof Error ? reason.message : '요청 상세를 불러오지 못했습니다.');
    }).finally(() => { if (current === sequence.current) setLoading(false); });
    if (retry > 0 && !fixtureMode && !task.decision) {
      setAssignmentLoading(true);
      void port.getWorkflowTask(task.stepId).then(value => {
        if (current !== sequence.current) return;
        if (!value || value.stepId !== task.stepId || value.step.id !== task.stepId || value.step.order !== task.step.order
          || workflowTaskDocumentId(value) !== documentId || (task.eventId && value.eventId && task.eventId !== value.eventId)) {
          throw new Error('선택한 할 일과 현재 배정 정보가 일치하지 않습니다. 최신 상태를 다시 불러와 주세요.');
        }
        if (value.decision || value.status !== 'IN_PROGRESS' || value.step.status !== 'IN_PROGRESS') {
          throw new Error('이 할 일은 이미 처리되었거나 현재 담당자에게 배정되어 있지 않습니다.');
        }
        setVerifiedAssignment(value);
      }).catch((reason: unknown) => {
        if (current !== sequence.current) return;
        setAssignmentError([403, 404].includes(workflowErrorStatus(reason) ?? 0)
          ? '이 할 일은 이미 처리되었거나 현재 담당자에게 배정되어 있지 않습니다.'
          : reason instanceof Error ? reason.message : '할 일 배정을 확인하지 못했습니다. 최신 상태를 다시 불러와 주세요.');
      }).finally(() => { if (current === sequence.current) setAssignmentLoading(false); });
    }
    return () => { sequence.current += 1; };
  }, [task?.stepId, documentId, port, fixtureMode, retry]);

  const validationError = assignmentError || (task && request && !task.decision
    ? workflowTaskDetailError(task, request) || (verifiedAssignment ? workflowTaskDetailError(verifiedAssignment, request) : undefined) : undefined);
  const cancellationDetailReady = task?.processType !== 'TIME_OFF_CANCELLATION' || Boolean(documentId || task.cancellation);
  const canDecide = cancellationDetailReady && allowed && !task?.decision && !conflict && task?.step.status === 'IN_PROGRESS' && task.stepId === task.step.id
    && !assignmentLoading && !legacyLoading && !legacyError && (!documentId || Boolean(request && !loading && !error && !validationError));
  const affirmativeLabel = request?.approvalSteps?.find(step => step.order === task?.step.order)?.approvalLabel?.name ?? '승인';
  const canReturn = Boolean(documentId && request && !request.target);
  const leave = (callback: () => void) => {
    if (skipGuard.current) { callback(); return; }
    if (pendingRef.current || commentBusy) return;
    retainAndNavigate(callback);
  };
  useEffect(() => {
    if (navigationRef) navigationRef.current = leave;
    return () => { if (navigationRef) navigationRef.current = null; };
  });
  const decide = async (action: WorkflowTaskDecision) => {
    if (!canDecide || !task || pendingRef.current || commentBusy || commentAttempts.current.has(task.stepId)) return;
    if (action !== 'approve' && !comment.trim()) {
      setCommentError(action === 'reject' ? '반려 사유를 의견에 입력해 주세요.' : '보완할 내용을 의견에 입력해 주세요.');
      opinionInput.current?.focus();
      return;
    }
    const previous = attempts.current.get(task.stepId);
    if (previous && (previous.action !== action || previous.comment !== comment)) return;
    const attempt = previous ?? { action, comment, key: crypto.randomUUID() };
    const activeTask = task;
    pendingRef.current = true; pendingMutation.current = attempt.key; setPending(true); setActionError('');
    try {
      if (!fixtureMode) {
        if (action === 'approve') await (actions?.approve ?? port.approveWorkflowTask)(task.stepId, attempt.comment, attempt.key);
        else if (action === 'reject') await (actions?.reject ?? port.rejectWorkflowTask)(task.stepId, attempt.comment, attempt.key);
        else await port.requestWorkflowTaskChanges(task.stepId, attempt.comment, attempt.key);
      }
      drafts.current.delete(task.stepId); attempts.current.delete(task.stepId);
      if (taskRef.current?.stepId !== task.stepId) return;
      setComment(''); setUncertain(undefined);
      let latestStatus: WorkflowRequest['status'] | undefined;
      if (!fixtureMode && documentId) {
        try { latestStatus = (await port.getWorkflowRequest(documentId)).status; } catch { /* 결정 성공은 후속 조회 실패로 되돌리지 않습니다. */ }
      }
      if (taskRef.current?.stepId !== task.stepId) return;
      const outcome = action === 'approve'
        ? `${activeTask.step.descriptor} 단계를 승인했습니다.${latestStatus === 'COMPLETED' ? ' 전체 결재가 완료되었습니다.' : latestStatus === 'IN_PROGRESS' ? ' 다른 담당자의 처리가 남아 있습니다.' : ''}`
        : action === 'reject' ? '반려했습니다. 이 요청의 결재가 종료되었습니다.' : '보완을 요청했습니다. 수정 후 제출하면 첫 단계부터 다시 결재합니다.';
      retainAndNavigate(() => onComplete(activeTask, action, `“${workflowTaskTitle(activeTask)}” ${outcome}`));
    } catch (reason) {
      if (isAmbiguousApiMutationFailure(reason)) attempts.current.set(task.stepId, attempt);
      else attempts.current.delete(task.stepId);
      if (taskRef.current?.stepId !== task.stepId) return;
      setActivityRefreshKey(value => value + 1);
      const stale = workflowErrorCode(reason) !== 'APPLICATION_FAILED' && [404, 409].includes(workflowErrorStatus(reason) ?? 0);
      setConflict(stale); setUncertain(attempts.current.get(task.stepId));
      setActionError(stale ? '이미 처리되었거나 담당자가 바뀌었습니다. 최신 상태를 확인해 주세요.' : reason instanceof Error ? reason.message : '요청을 처리하지 못했습니다.');
    } finally { if (pendingMutation.current === attempt.key) { pendingRef.current = false; pendingMutation.current = ''; setPending(false); setConfirmation(undefined); } }
  };
  const beginDecision = (action: WorkflowTaskDecision) => {
    if (!canDecide || busy) return;
    if (action === 'approve' || uncertain || !comment.trim()) { void decide(action); return; }
    setConfirmation(action);
  };
  const changeComment = (value: string) => {
    setComment(value);
    if (task) { if (value) drafts.current.set(task.stepId, value); else drafts.current.delete(task.stepId); }
    setActionError(''); setCommentError('');
  };
  const rememberCommentAttempt = (value: WorkflowCommentAttempt | undefined) => {
    if (!task) return;
    if (value) commentAttempts.current.set(task.stepId, value);
    else commentAttempts.current.delete(task.stepId);
    setCommentAttempt(value);
  };
  const errorAction = conflict || validationError ? <Button onClick={() => { setRetry(value => value + 1); onRefresh(); }}>최신 상태 불러오기</Button> : undefined;
  const decisionActions = canDecide ? <>
    {canReturn ? <Button size={documentId ? 'small' : 'medium'} variant="outlined" disabled={busy || Boolean(commentAttempt) || Boolean(uncertain && uncertain.action !== 'requestChanges')} onClick={() => beginDecision('requestChanges')}>{uncertain?.action === 'requestChanges' ? '보완 요청 다시 시도' : '보완 요청'}</Button> : null}
    <Button size={documentId ? 'small' : 'medium'} color="error" variant="outlined" disabled={busy || Boolean(commentAttempt) || Boolean(uncertain && uncertain.action !== 'reject')} onClick={() => beginDecision('reject')}>{uncertain?.action === 'reject' ? '반려 다시 시도' : '반려'}</Button>
    <Button size={documentId ? 'small' : 'medium'} variant="contained" disabled={busy || Boolean(commentAttempt) || Boolean(uncertain && uncertain.action !== 'approve')} onClick={() => beginDecision('approve')}>{uncertain?.action === 'approve' ? `${affirmativeLabel} 다시 시도` : affirmativeLabel}</Button>
  </> : errorAction;
  const footer = canDecide ? <>
    {uncertain ? <Alert severity="warning" sx={{ flexBasis: '100%' }}>처리 결과를 확인하지 못했습니다. 의견과 처리 방식을 유지한 채 다시 시도해 주세요.</Alert> : null}
    <FormTextField label="처리 의견" helperText={commentError || '반려하면 이 요청의 결재가 종료됩니다.'} error={Boolean(commentError)} inputRef={opinionInput} value={comment}
      onChange={event => changeComment(event.target.value)}
      disabled={busy || Boolean(uncertain)} multiline minRows={1} maxRows={4} sx={{ flex: '1 1 420px' }} />
    {decisionActions}
  </> : errorAction;
  const requestAcceptsComment = Boolean(request && ['IN_PROGRESS', 'NEEDS_CHANGES', 'COMPLETED'].includes(request.status));
  const recovery = !loading && !assignmentLoading && request && !canDecide && !(canComment && requestAcceptsComment) && comment.trim() && !commentAttempt && !uncertain
    ? { message: requestAcceptsComment ? '현재는 결재하거나 댓글을 등록할 수 없습니다. 작성한 의견을 복사하거나 폐기할 수 있습니다.' : '요청이 종료되어 이 의견을 전송할 수 없습니다. 작성한 내용을 복사하거나 폐기할 수 있습니다.', onDiscard: () => changeComment('') } : undefined;
  const confirmationDialog = <ConfirmDialog open={Boolean(confirmation)} title={confirmation === 'reject' ? '요청을 반려할까요?' : '보완을 요청할까요?'}
    description={confirmation === 'reject' ? `이 요청의 결재가 종료됩니다.${canReturn ? ' 내용을 수정해서 다시 결재받아야 한다면 보완 요청을 선택해 주세요.' : ''}` : '요청자에게 수정을 요청합니다. 수정 후 다시 제출하면 첫 단계부터 결재가 시작됩니다.'}
    confirmLabel={confirmation === 'reject' ? '반려 확정' : '보완 요청 확정'} cancelLabel="계속 검토" intent={confirmation === 'reject' ? 'discard' : 'confirm'}
    busy={pending} onCancel={() => setConfirmation(undefined)} onConfirm={() => confirmation ? decide(confirmation) : undefined}>
    <Stack spacing={1}><Typography variant="body2" sx={{ fontWeight: 600 }}>보낼 의견</Typography><Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{comment}</Typography></Stack>
  </ConfirmDialog>;
  if (!task) return null;
  if (documentId) return <><WorkflowRequestDocumentWorkspace port={port} approvalSettingsPort={approvalSettingsPort} directoryPort={directoryPort}
    request={request ?? workflowRequestSummaryFromInboxItem(task)} taskDueAt={task.dueAt} fixtureMode={fixtureMode} loading={loading} error={error || undefined}
    activityRefreshKey={activityRefreshKey}
    actionableStepOrder={canDecide && !pending && !uncertain && !commentAttempt ? task.step.order : undefined}
    onRefreshRequest={async () => {
      const current = sequence.current;
      const latest = await port.getWorkflowRequest(documentId);
      if (current !== sequence.current) return;
      if (latest.id !== documentId) throw new Error('요청 상태를 확인하지 못했습니다. 다시 확인해 주세요.');
      setRequest(latest); onRefresh();
    }}
    onRetry={error ? () => setRetry(value => value + 1) : undefined} actionError={actionError || validationError} busy={busy}
    canComment={Boolean(request) && !loading && !error && canComment} onClose={() => leave(onClose)}
    activityComposer={{ value: comment, onChange: changeComment, canEdit: Boolean(canDecide), disabled: pending || Boolean(uncertain), draftKey: task.stepId,
      commentAttempt, onCommentAttempt: rememberCommentAttempt, actions: decisionActions, error: commentError || undefined, inputRef: opinionInput, recovery,
      helperText: canDecide && !canComment ? '의견은 선택한 처리와 함께 기록됩니다.' : undefined,
      notice: uncertain || actionError || validationError ? <>{uncertain ? <Alert severity="warning">처리 결과를 확인하지 못했습니다. 의견과 처리 방식을 유지한 채 다시 시도해 주세요.</Alert> : null}{actionError || validationError ? <Alert severity="error">{actionError || validationError}</Alert> : null}</> : undefined }}
    presentation={presentation} onPresentationChange={value => leave(() => onPresentationChange(value))}
    onCommentBusyChange={setCommentBusy}
    onOpenReference={onOpenReference ? id => { if (!pendingRef.current && !commentBusy) onOpenReference(id); } : undefined} />{confirmationDialog}</>;
  return <><DetailDrawer open onClose={() => leave(onClose)} title={workflowTaskTitle(task)} meta={task.step.descriptor}
    footer={footer ? <Stack spacing={1}>{actionError ? <Alert severity="error">{actionError}</Alert> : null}<Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>{footer}</Stack></Stack> : undefined}>
    <DetailDrawerBody spacing={2}>
      {task.cancellation ? <TimeOffCancellationSummary values={task.cancellation} status={task.decision ? undefined : 'IN_PROGRESS'} originalRequestHref={task.cancellation.originalWorkflowRequestId ? port.workflowRequestPath?.(task.cancellation.originalWorkflowRequestId) : undefined} />
        : task.processType === 'TIME_OFF_CANCELLATION' ? <Alert severity="error">취소할 휴가 정보를 불러오지 못했습니다. 목록을 새로고침해 주세요.</Alert> : null}
      <SectionCard title={<Typography component="h2" variant="h6">요청 정보</Typography>} showDivider={false} bodyPadding="standard"><Stack spacing={2}>
      <DefinitionList items={[["요청자", workflowRequesterLabel(task.requester.descriptor)], ['현재 단계', task.step.descriptor], ['요청일', formatWorkflowDate(task.requestedAt)], ['대기 기간', workflowTaskWaitingLabel(task.assignedAt ?? task.requestedAt)]]} />
      {legacyError ? <Alert severity="error" action={onRetryLegacy ? <Button onClick={onRetryLegacy}>다시 불러오기</Button> : undefined}>{legacyError}</Alert> : legacyDetail}
      {!legacyDetail && !legacyError && task.processType !== 'TIME_OFF_CANCELLATION' ? <Typography color="text.secondary">연결된 요청 문서가 없습니다. 업무 정보와 대상을 확인한 뒤 처리해 주세요.</Typography> : null}
      </Stack></SectionCard>
    </DetailDrawerBody>
  </DetailDrawer>{confirmationDialog}</>;
}
